import {
  canonicalJson,
  channelIdForOwner,
  GymSchema,
  MemberScheduleSchema,
  normalizeGym,
  normalizeMemberSchedule,
  normalizeOccupancyDigest,
  OccupancyDigestSchema,
  occupancyFingerprint,
  opaqueChannelId,
  pruneOccupancyDigest,
  type OccupancyDigest,
  type RoleCertificate,
} from "@palestra/core";
import type { LocalProfile } from "./app-store";
import {
  getMeta,
  loadDocument,
  loadGymDocuments,
  loadMemberScheduleDocument,
  loadOccupancyDocument,
} from "./storage";

export function documentFingerprint(document: unknown): string {
  return opaqueChannelId(canonicalJson(document));
}

/** Le date di generazione non sono cambiamenti della capienza. */
export function occupancyInventoryFingerprint(digest: OccupancyDigest, fromDate: string): string {
  return opaqueChannelId(
    occupancyFingerprint(normalizeOccupancyDigest(pruneOccupancyDigest(digest, fromDate))),
  );
}

/** Si dichiarano solo documenti realmente salvati, mai copie soltanto in memoria. */
export async function alignmentInventory(
  profile: LocalProfile,
  fromDate: string,
): Promise<Record<string, string>> {
  const known: Record<string, string> = {};
  const remember = (key: string, document: unknown) => {
    // Gli elementi omessi verranno ritrasmessi: il limite non può nascondere dati mancanti.
    if (Object.keys(known).length < 1000) known[key] = documentFingerprint(document);
  };
  const gym = await loadGymDocuments(profile.gymId);
  if (gym && !(await getMeta<boolean>("awaitingInitialSync")))
    remember("gym", normalizeGym(GymSchema.parse(gym)));
  for (const id of (await getMeta<string[]>("scheduleIds")) ?? []) {
    const schedule = await loadMemberScheduleDocument(profile.gymId, id);
    if (schedule)
      remember(`schedule:${id}`, normalizeMemberSchedule(MemberScheduleSchema.parse(schedule)));
  }
  if (profile.role === "member") {
    const occupancy = await loadOccupancyDocument(profile.gymId);
    if (occupancy && Object.keys(known).length < 1000)
      known.occupancy = occupancyInventoryFingerprint(
        OccupancyDigestSchema.parse(occupancy),
        fromDate,
      );
  } else {
    for (const peer of profile.peers) {
      const certificate = await loadDocument<RoleCertificate>(
        `admin:${profile.gymId}:role:${peer.id}`,
      );
      if (certificate) remember(`role:${peer.id}`, certificate);
    }
    for (const revocation of profile.revokedPeers ?? [])
      remember(`revocation:${revocation.subjectId}`, revocation);
  }
  return known;
}

export function needsChannelPass(
  profile: LocalProfile,
  signingPublicKey: string,
  identityId: string,
): boolean {
  if (profile.channelId === channelIdForOwner(signingPublicKey)) return false;
  const proof = profile.channelProof;
  return (
    !proof?.length ||
    proof.at(-1)?.subjectId !== identityId ||
    proof.some(
      (pass) => pass.expiresAt && Date.parse(pass.expiresAt) < Date.now() + 24 * 60 * 60 * 1000,
    )
  );
}
